// UI-DIRECTORY-1 (2026-05-18) — unified Companies + Contacts search surface.
//
// Reads GET /directory/search?q=... which returns mixed company + contact
// rows.  Clicking a result navigates to existing Companies / Contacts
// detail pages (no new detail UI in v1 — composes existing endpoints).
//
// Privacy: search results return company/contact rows only (no claim
// inventory).  Related-claims queries on detail pages stay on the existing
// scoped /companies/{id}/claims and /contacts/{id}/claims endpoints which
// already enforce VIEW_TEAM_REPORTS.
//
// Vocabulary (per CLAUDE.md canonical party terms):
//   Insured = policy-holding company
//   Claimant = injured person (PHI)
//   Rep = contact at Insured company
//   Underwriter / Lead Underwriter = carrier/syndicate
//
// IA note: Companies + Contacts pages remain in nav as deprecated peers
// for one transition cycle.  Directory is the new preferred surface;
// removal of the deprecated peers is scheduled as UI-DIRECTORY-1-FOLLOWUP.

import { useState, useEffect, useMemo } from "react";
import { apiFetch, can } from "../api";
import { Spinner, EmptyState, SectionHeader, SummaryRibbon, RibbonCell } from "../components/ui";  // UI-SWEEP-DIRECTORY
import { CreateCompanyModal } from "./Companies";
import EditContactModal from "../components/EditContactModal";  // CONTACT-CREATE-1: create mode (contact={null})
import PayeeProfileModal from "../components/PayeeProfileModal";

const KIND_OPTIONS = [
  { id: "all",       label: "All" },
  { id: "company",   label: "Companies" },
  { id: "contact",   label: "Contacts" },
  { id: "claimant",  label: "Claimants" },
];

export default function Directory({ currentUser, onNavigateToCompany, onNavigateToContact, onNavigateToClaim }) {
  const [q, setQ]                 = useState("");
  const [kind, setKind]           = useState("all");
  const [payeeFor, setPayeeFor]   = useState(null);
  const [debouncedQ, setDebouncedQ] = useState("");
  const [results, setResults]     = useState({ companies: [], contacts: [], claimants: [], query: "" });
  const [loading, setLoading]     = useState(false);
  const [err, setErr]             = useState(null);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [creatingContact, setCreatingContact] = useState(false);  // CONTACT-CREATE-1
  const [refreshTick, setRefreshTick]         = useState(0);      // bump to re-run an active search after a create
  const [schedules, setSchedules] = useState([]);
  useEffect(() => { apiFetch("/rate-schedules").then(setSchedules).catch(() => {}); }, []);

  // Simple debounce on the search term so we don't fire on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!debouncedQ || debouncedQ.length < 2) {
      setResults({ companies: [], contacts: [], claimants: [], query: debouncedQ });
      setErr(null);
      return;
    }
    setLoading(true);
    setErr(null);
    const params = new URLSearchParams({ q: debouncedQ, kind, limit: 50 });
    apiFetch(`/directory/search?${params.toString()}`)
      .then(d => { setResults(d || { companies: [], contacts: [] }); setLoading(false); })
      .catch(e => { setErr(String(e.message || e)); setLoading(false); });
  }, [debouncedQ, kind, refreshTick]);

  const companyCount = results.companies?.length || 0;
  const contactCount = results.contacts?.length || 0;
  const claimantCount = results.claimants?.length || 0;
  const total = companyCount + contactCount + claimantCount;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Directory</div>
          <div className="page-subtitle">Search across companies, contacts, and claimants. Click a result to open it.</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-blue btn-sm" onClick={() => setCreatingContact(true)}>+ New Contact</button>
          <button className="btn btn-blue btn-sm" onClick={() => setCreatingCompany(true)}>+ New Company</button>
        </div>
      </div>

      {/* LD-DESIGN-010 SummaryRibbon — result-scoped vitals; only when a search returned results. */}
      {debouncedQ && debouncedQ.length >= 2 && !loading && !err && total > 0 && (
        <SummaryRibbon>{[
          <RibbonCell key="co" icon="🏢" label="Companies" value={companyCount} />,
          <RibbonCell key="ct" icon="👤" label="Contacts" value={contactCount} />,
          <RibbonCell key="cm" icon="🧍" label="Claimants" value={claimantCount} />,
          <RibbonCell key="tot" label="Total" value={total} color="var(--navy)" sub={`for “${debouncedQ}”`} />,
        ]}</SummaryRibbon>
      )}

      <div style={{
        display: "flex", gap: 8, marginBottom: 16, alignItems: "center",
      }}>
        <input
          type="text"
          placeholder="Search by company, contact, email, or claimant name…"
          value={q}
          onChange={e => setQ(e.target.value)}
          autoFocus
          style={{
            flex: 1, padding: "8px 12px", fontSize: 14,
            border: "1px solid var(--border,#DDE2E8)", borderRadius: 6,
          }}
        />
        <div style={{ display: "flex", gap: 4 }}>
          {KIND_OPTIONS.map(opt => (
            <button
              key={opt.id}
              onClick={() => setKind(opt.id)}
              style={{
                padding: "6px 12px", fontSize: 12,
                border: `1px solid ${kind === opt.id ? "var(--navy,#1F3864)" : "var(--border,#DDE2E8)"}`,
                background: kind === opt.id ? "var(--navy,#1F3864)" : "white",
                color: kind === opt.id ? "white" : "var(--text,#333)",
                borderRadius: 4, cursor: "pointer",
                fontWeight: kind === opt.id ? 600 : 400,
              }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Result count / state */}
      {!debouncedQ || debouncedQ.length < 2 ? (
        <div className="card"><EmptyState icon="🔍" title="Start searching"
          hint="Start typing to search across companies, contacts, and claimants." /></div>
      ) : loading ? (
        <Spinner label="Searching…" />
      ) : err ? (
        <div className="alert-error">{err}</div>
      ) : total === 0 ? (
        <div className="card"><EmptyState icon="🔍" title="No results"
          hint={`No companies, contacts, or claimants match "${debouncedQ}".`} /></div>
      ) : (
        <>
          {companyCount > 0 && (
            <ResultSection
              title="Companies"
              count={companyCount}
              rows={results.companies}
              renderRow={c => <CompanyRow key={`co-${c.id}`} c={c} onClick={() => onNavigateToCompany?.(c.id)} onPayee={can("view_payees") ? () => setPayeeFor({ kind: "company", id: c.id, name: c.company_name }) : null} />}
            />
          )}
          {contactCount > 0 && (
            <ResultSection
              title="Contacts"
              count={contactCount}
              rows={results.contacts}
              renderRow={c => <ContactRow key={`ct-${c.id}`} c={c} onClick={() => onNavigateToContact?.(c.id)} onPayee={can("view_payees") ? () => setPayeeFor({ kind: "contact", id: c.id, name: [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email }) : null} />}
            />
          )}
          {claimantCount > 0 && (
            <ResultSection
              title="Claimants"
              count={claimantCount}
              rows={results.claimants}
              renderRow={c => <ClaimantRow key={`cm-${c.claim_id}`} c={c} onClick={() => onNavigateToClaim?.(c.claim_id)} />}
            />
          )}
        </>
      )}

      {creatingCompany && (
        <CreateCompanyModal
          schedules={schedules}
          isAdmin={can("approve_companies")}
          onClose={() => setCreatingCompany(false)}
          onCreated={() => setCreatingCompany(false)}
        />
      )}

      {/* CONTACT-CREATE-1: shared modal in create mode; company picked inside
          (optional, per contact schema). Re-runs the search if one is active. */}
      {creatingContact && (
        <EditContactModal
          contact={null}
          isAdmin={can("approve_contacts")}
          onClose={() => setCreatingContact(false)}
          onCreated={() => { setCreatingContact(false); setRefreshTick(t => t + 1); }}
        />
      )}

      {payeeFor && (
        <PayeeProfileModal kind={payeeFor.kind} entityId={payeeFor.id} name={payeeFor.name} onClose={() => setPayeeFor(null)} />
      )}
    </div>
  );
}

function ResultSection({ title, count, rows, renderRow }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <SectionHeader title={<>{title} <span style={{ color: "var(--text3)", fontWeight: 400 }}>({count})</span></>} />
      <div className="card" style={{ padding: 0 }}>
        {rows.map(renderRow)}
      </div>
    </div>
  );
}

function CompanyRow({ c, onClick, onPayee }) {
  return (
    <div onClick={onClick}
         style={{
           padding: "10px 16px", cursor: "pointer",
           borderBottom: "1px solid var(--border-light,#EEF1F4)",
           display: "flex", alignItems: "center", gap: 12,
         }}
         onMouseEnter={e => e.currentTarget.style.background = "var(--bg,#F8F9FB)"}
         onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text,#1A1A1A)" }}>
          {c.company_name}
        </div>
        <div style={{ fontSize: 12, color: "var(--text3,#888)", marginTop: 2 }}>
          {[c.company_type, [c.city, c.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ") || "—"}
        </div>
      </div>
      {onPayee && <button className="btn btn-sm btn-outline" onClick={e => { e.stopPropagation(); onPayee(); }} style={{ fontSize: 11 }}>$ Payee</button>}
      <span style={{
        fontSize: 10, padding: "2px 7px", borderRadius: 3,
        background: "var(--blue-light,#E8F0F8)", color: "var(--navy,#1F3864)",
        textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600,
      }}>Company</span>
    </div>
  );
}

// CLAIMANT-SEARCH-1: a claimant result is a claim (claimant = injured person on a claim,
// PHI). Click navigates to the claim. Visibility is gated server-side (_claims_visibility).
function ClaimantRow({ c, onClick }) {
  return (
    <div onClick={onClick}
         style={{ padding: "10px 16px", cursor: "pointer", borderBottom: "1px solid var(--border-light,#EEF1F4)", display: "flex", alignItems: "center", gap: 12 }}
         onMouseEnter={e => e.currentTarget.style.background = "var(--bg,#F8F9FB)"}
         onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text,#1A1A1A)" }}>
          {c.claimant_name || "(unnamed)"}
          <span style={{ marginLeft: 8, fontSize: 10, padding: "1px 6px", borderRadius: 3, background: "#fee2e2", color: "#991b1b", fontWeight: 700 }}>PHI</span>
        </div>
        <div style={{ fontSize: 12, color: "var(--text3,#888)", marginTop: 2 }}>
          {[c.claim_number, c.matter_type ? String(c.matter_type).toUpperCase() : null, c.status].filter(Boolean).join(" · ") || "—"}
        </div>
      </div>
      <span style={{
        fontSize: 10, padding: "2px 7px", borderRadius: 3,
        background: "var(--blue-light,#E8F0F8)", color: "var(--navy,#1F3864)",
        textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600,
      }}>Claimant</span>
    </div>
  );
}

function ContactRow({ c, onClick, onPayee }) {
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "(no name)";
  return (
    <div onClick={onClick}
         style={{
           padding: "10px 16px", cursor: "pointer",
           borderBottom: "1px solid var(--border-light,#EEF1F4)",
           display: "flex", alignItems: "center", gap: 12,
         }}
         onMouseEnter={e => e.currentTarget.style.background = "var(--bg,#F8F9FB)"}
         onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text,#1A1A1A)" }}>
          {name}
        </div>
        <div style={{ fontSize: 12, color: "var(--text3,#888)", marginTop: 2 }}>
          {[c.title, c.company_name, c.email].filter(Boolean).join(" · ") || "—"}
        </div>
      </div>
      {onPayee && <button className="btn btn-sm btn-outline" onClick={e => { e.stopPropagation(); onPayee(); }} style={{ fontSize: 11 }}>$ Payee</button>}
      <span style={{
        fontSize: 10, padding: "2px 7px", borderRadius: 3,
        background: "#F3E5F5", color: "#6A1B9A",
        textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600,
      }}>Contact</span>
    </div>
  );
}
